import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { Link } from "react-router-dom";
import { Modal, ModalBody, ModalFooter, Button, Input } from "reactstrap";
import i18n from "../../../i18n";
import { postCreateNotesServices, getListNotesServices } from "../../../helpers/api_rombo_helper";
import Paginations from "../../../components/Common/Pagination";
import plusCircle from "../../../assets/images/plus-circle.png";
import minusCircle from "../../../assets/images/minus-circle.png";

const NotesServiceModal = ({ isOpen, toggle, selectedService }) => {

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;
  const [totalNotes, setTotalNotes] = useState(0);
  const [notes, setNotes] = useState([]);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [note, setNote] = useState("");
  const [isNoteValid, setIsNoteValid] = useState(false);
  const [showAddNoteForm, setShowAddNoteForm] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({
      token: Login.token,
      loading: Login.loading,
      data: Login.data,
    })
  );

  const { token } = useSelector(LoginProperties);

  const fetchNotes = async () => {
    if (!token || !selectedService?.code) return;
  
    try {
      const response = await getListNotesServices(token, selectedService.code, {
        page: currentPage,
        size: pageSize,
      });
  
      setNotes(response.items || []);
      setTotalNotes(response.total || 0);
    } catch (error) {
      console.error("Error getting notes:", error);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [selectedService.code, token, currentPage]);
  
  const handleAddNote = async () => {
    if (!token) {
      console.error("Token not found.");
      return;
    }

    setIsLoading(true);

    const path = selectedService.code;
    const payload = {
      note,
      confirmed,
      timezone: "America/Mexico_City",
    };

    try {
      await postCreateNotesServices(token, path, payload);
      setNote("");
      setConfirmed(false);
      setShowAddNoteForm(false);
      fetchNotes();
    } catch (error) {
      console.error("Error adding note:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleNoteExpansion = (id) => {
    setExpandedNotes((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      role="dialog"
      autoFocus={false}
      centered
      className="RequestModal"
      style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      toggle={toggle}
      backdrop="static"
      size="lg"
    >
      <div>
        <div className="modal-header-container">
          <p className="cancellation-title modal-header-bottom">
            {showAddNoteForm ? i18n.t("AddNoteToService") : i18n.t("NotesForService")}
          </p>
          <p className="cancellation-text modal-service-id">{selectedService.code}</p>
        </div>
        <div className="modal-separator" />
        <ModalBody>
          {!showAddNoteForm && (
            <>
              {notes.length > 0 ? (
                <>
                  {notes.map((note) => (
                    <div key={note.id} className="note-container">
                      <div className="notes-container">
                        <div className="notes-column">
                          <p className="cancellation-text add-scale-text">
                            {i18n.t("DateAndTimeOfTheNote")}
                          </p>
                          <p className="cancellation-text">
                            {new Date(note.created_at).toLocaleDateString('es-ES', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'
                            })}
                            <br />
                            {new Date(note.created_at).toLocaleTimeString('es-ES', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            })}
                          </p>
                        </div>
                        <div className="divider" />
                        <div className="notes-column">
                          {note.note.length > 100 && (
                            <img
                              src={expandedNotes[note.id] ? minusCircle : plusCircle}
                              alt={expandedNotes[note.id] ? "Collapse Note" : "Expand Note"}
                              className="circle-icon"
                              onClick={() => toggleNoteExpansion(note.id)}
                              style={{ cursor: "pointer" }}
                            />                         
                          )}
                          <p className="cancellation-text add-scale-text">{i18n.t("Note")}:</p>
                          <p className="cancellation-text notes-text">
                            {expandedNotes[note.id] ? note.note : `${note.note.substring(0, 100)}...`}
                          </p>
                        </div>
                        <div className="divider" />
                        <div className="notes-column">
                          <p className="cancellation-text add-scale-text">{i18n.t("Confirmed")}:</p>
                          <p className="cancellation-text">
                            {note.confirmed ? i18n.t("Yes") : i18n.t("No")}
                          </p>
                        </div>
                      </div>
                      <div className="modal-separator" />
                    </div>
                  ))}
                  {totalNotes > pageSize && (
                    <div className="pagination-container">
                      <Paginations
                        perPageData={pageSize}
                        totalNotes={totalNotes}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        isShowingPageLength={true}
                        paginationDiv="col-lg-12"
                        paginationClass="pagination pagination-rounded justify-content-center mt-3 mb-4 pb-1"
                      />
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p className="cancellation-text">{i18n.t("NoNotes")}</p>
                  <div className="modal-separator" />
                </>
              )}
              <p/>
              <Link className="add-scale-text" onClick={() => setShowAddNoteForm(true)}>
                <i className="bx bx-plus add-scale-icon" />
                {i18n.t("AddNote")}
              </Link>
            </>
          )}
          {showAddNoteForm && (
            <>
              <Input
                type="textarea"
                autoFocus
                className="form-control"
                placeholder={i18n.t("WriteYourNoteHere")}
                value={note}
                maxLength={200}
                onChange={(e) => {
                  setNote(e.target.value.slice(0, 200));
                  setIsNoteValid(e.target.value.trim() !== "");
                }}
              />
              <small className="text-muted">{note.length}/200 {i18n.t("Characters")}</small>
              <label className="checkbox-label checkbox-margin-top">
                <input
                  type="checkbox"
                  onClick={() => setConfirmed((prev) => !prev)}
                  className="modal-checkbox"
                  checked={confirmed}
                />
                {i18n.t("ConfirmedService")}
              </label>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {showAddNoteForm && (
            <Button
              className="btn btn-users border"
              type="button"
              onClick={handleAddNote}
              disabled={!isNoteValid || isLoading}
            >
              {isLoading ? i18n.t("Adding...") : i18n.t("Add")}
            </Button>
          )}
          <Button
            className="btn btn-light"
            type="button"
            onClick={() => (showAddNoteForm ? setShowAddNoteForm(false) : toggle())}
          >
            {showAddNoteForm ? i18n.t("Cancel") : i18n.t("GoBack")}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

NotesServiceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  selectedService: PropTypes.object.isRequired,
};

export default NotesServiceModal;
